import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import TenantList from "../../components/admin/TenantList";
import { fetchTenants } from "../../api/tenantApi";
import useAuth from "../../hooks/useAuth";
import { isPlatformAdmin, isTenantAdmin } from "../../util/accessControl";
import LoadingState from "../../components/admin/LoadingState";

const TenantListPage = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [tenants, setTenants] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	const myTenantId = user?.tenantId;

	const loadAllTenants = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const response = await fetchTenants();
			const items = response?.data?.items || response?.data || response || [];
			setTenants(Array.isArray(items) ? items : []);
		} catch (fetchError) {
			setError(fetchError.message || "Failed to load tenants.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadMyTenant = useCallback(async () => {
		setLoading(true);
		try {
			const response = await fetchTenants();
			const items = response?.data?.items || response?.data || response || [];
			const allTenants = Array.isArray(items) ? items : [];
			const myTenant = allTenants.find((t) => String(t.id) === String(myTenantId));
			setTenants(myTenant ? [myTenant] : []);
		} catch (fetchError) {
			setError(fetchError.message || "Failed to load tenant.");
		} finally {
			setLoading(false);
		}
	}, [myTenantId]);

	useEffect(() => {
		if (isPlatformAdmin(user)) {
			loadAllTenants();
		} else if (myTenantId) {
			loadMyTenant();
		} else {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Tenant admin: auto-redirect to own tenant (after hooks)
	if (isTenantAdmin(user) && myTenantId) {
		return <Navigate to={`/admin/tenants/${myTenantId}`} replace />;
	}

	const isSingleTenantView = !isPlatformAdmin(user) && tenants.length <= 1;
	const canCreate = isPlatformAdmin(user);

	if (loading) return <LoadingState label="Loading tenants..." />;

	return (
		<TenantList
			tenants={tenants}
			loading={loading}
			error={error}
			search={search}
			statusFilter={statusFilter}
			onSearchChange={setSearch}
			onStatusChange={setStatusFilter}
			onCreate={canCreate ? () => navigate("/admin/tenants/create") : undefined}
			onClearFilters={() => {
				setSearch("");
				setStatusFilter("all");
			}}
			onRetry={isPlatformAdmin(user) ? loadAllTenants : loadMyTenant}
			singleTenantMode={isSingleTenantView}
		/>
	);
};

export default TenantListPage;
